import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "收藏夹", to: "/favorites" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search?q=");
    setMobileOpen(false);
  };

  const isCategoryActive = location.pathname.startsWith("/category");

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/80 bg-background-50/90 backdrop-blur-lg">
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[72px] md:px-8 lg:px-12">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50">
            <i className="ri-film-line text-lg"></i>
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-[17px] font-semibold tracking-wide text-foreground-950">夜澜</span>
            <span className="block font-label text-[9px] tracking-[0.24em] text-foreground-500">日韩伦理电影</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className={`rounded-md px-3 py-2 text-[13px] transition-colors ${
              location.pathname === "/"
                ? "text-primary-300"
                : "text-foreground-600 hover:text-foreground-950"
            }`}
          >
            首页
          </Link>

          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              className={`flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-[13px] transition-colors ${
                isCategoryActive ? "text-primary-300" : "text-foreground-600 hover:text-foreground-950"
              }`}
              aria-expanded={categoryOpen}
            >
              分类目录
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full w-[320px] pt-2">
                <div className="animate-fade-in rounded-lg border border-background-200 bg-background-100 p-2">
                  {categories.map((category) => (
                    <Link
                      key={category.key}
                      to={`/category/${category.key}`}
                      className="group flex items-start gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-200/70"
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-900/60 text-accent-200">
                        <i className={`${category.icon} text-base`}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 text-[13px] font-medium text-foreground-800 group-hover:text-primary-200">
                          {category.label}电影
                          <span className="font-label text-[10px] uppercase tracking-widest text-foreground-500">
                            {category.en}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-foreground-500">{category.tagline}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-md px-3 py-2 text-[13px] transition-colors ${
                location.pathname === link.to
                  ? "text-primary-300"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <form onSubmit={submitSearch} className="relative hidden lg:block" role="search">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索片名、导演、关键词"
              aria-label="站内搜索"
              className="h-9 w-[260px] rounded-md border border-background-300/80 bg-background-100 pl-9 pr-3 text-[13px] text-foreground-800 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500"
            />
          </form>

          <span className="hidden items-center rounded-md border border-primary-700/60 px-2 py-1 font-label text-[10px] font-semibold tracking-widest text-primary-300 sm:flex">
            18+
          </span>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/80 text-foreground-700 md:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-10 pt-4 md:hidden">
          <form onSubmit={submitSearch} className="relative mb-5" role="search">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索片名、导演、关键词"
              aria-label="站内搜索"
              className="h-11 w-full rounded-md border border-background-300/80 bg-background-100 pl-10 pr-3 text-sm text-foreground-800 outline-none placeholder:text-foreground-500 focus:border-primary-500"
            />
          </form>

          <div className="mb-2 flex flex-col">
            <Link to="/" className="border-b border-background-200/70 py-3.5 text-sm text-foreground-800">
              首页
            </Link>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="border-b border-background-200/70 py-3.5 text-sm text-foreground-800"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <p className="mb-2 mt-6 font-label text-[11px] uppercase tracking-[0.24em] text-foreground-500">分类目录</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((category) => (
              <Link
                key={category.key}
                to={`/category/${category.key}`}
                className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-3 text-[13px] text-foreground-700"
              >
                <i className={`${category.icon} text-primary-300`}></i>
                {category.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}